import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_NEXT_RESERVATION } from '../graphql/queries';

const formatTime = (timeString) => {
  if (!timeString) return '';

  try {
    // Handle cases where timeString is already in 12-hour format
    if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
      return timeString;
    }

    // Handle 24-hour format
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString; // Return original if parsing fails
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM

    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return timeString;
  }
};

export default function NextCheckInDisplay({ job }) {
  // Skip query if we don't have job data or scheduled date
  const shouldSkip = !job || !job.property || !job.property.id || !job.scheduledDate;

  const date = job?.scheduledDate ? new Date(job.scheduledDate) : new Date();
  const dateString = date.toISOString();

  const { data, loading, error } = useQuery(GET_NEXT_RESERVATION, {
    variables: {
      propertyId: job?.property?.id,
      date: dateString
    },
    skip: shouldSkip,
    fetchPolicy: 'cache-first', // Changed from 'cache-and-network' - reservation data is relatively static
    onError: (err) => {
      // Error handling without console logging
    },
    onCompleted: (data) => {
      // Processing completed without console logging
    }
  });

  if (loading || shouldSkip) {
    return <span className="text-gray-500">Loading...</span>;
  }

  if (error) {
    return <span className="text-red-500">Error loading</span>;
  }

  const nextReservation = data?.nextReservationForProperty;

  if (!nextReservation) {
    return <span className="text-gray-500">No Next Check-in</span>;
  }

  try {
    // Format the next check-in date
    const checkInDate = new Date(nextReservation.check_in);
    const month = checkInDate.getMonth() + 1;
    const day = checkInDate.getDate();

    // Get check-in time
    let checkInTime;
    if (nextReservation.has_time_override && nextReservation.override_check_in) {
      const overrideDate = new Date(nextReservation.override_check_in);
      const hours = overrideDate.getHours().toString().padStart(2, '0');
      const minutes = overrideDate.getMinutes().toString().padStart(2, '0');
      checkInTime = `${hours}:${minutes}`;
    } else {
      // Safely access the nested property
      checkInTime = nextReservation.check_in_time ||
                   (nextReservation.property?.checkInTime) ||
                   '15:00';
    }

    // Format as: "MM/DD at 3:00 PM"
    return <span>{month}/{day} at {formatTime(checkInTime)}</span>;
  } catch (err) {
    return <span className="text-red-500">Format error</span>;
  }
}
